import React from 'react';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createStackNavigator } from '@react-navigation/stack';
import { Platform, Text } from 'react-native';
import { useSelector } from 'react-redux';
import { colors } from '../constants/theme';
import { SearchTrigger } from '../components/SearchTrigger';
import { RootState } from '../store';

// Import MaterialCommunityIcons conditionally
let MaterialCommunityIcons: any;
if (Platform.OS !== 'web') {
  MaterialCommunityIcons = require('@expo/vector-icons').MaterialCommunityIcons;
}

// Import screens (to be created)
import HomeScreen from '../screens/HomeScreen';
import ReviewListScreen from '../screens/review/ReviewListScreen';
import ReviewDetailScreen from '../screens/review/ReviewDetailScreen';
import ItemDetailScreen from '../screens/review/ItemDetailScreen';
import VendorEmailsScreen from '../screens/VendorEmailsScreen';
import ResultsScreen from '../screens/results/ResultsScreen';
import ResultDetailScreen from '../screens/results/ResultDetailScreen';
import ProfileScreen from '../screens/ProfileScreen';
import AdminScreen from '../screens/admin/AdminScreen';
import ChangePasswordScreen from '../screens/auth/ChangePasswordScreen';

export type ReviewStackParamList = {
  ReviewList: undefined;
  ReviewDetail: { reviewId: number };
  ItemDetail: { reviewId: number; itemId: number; categoryName: string };
  VendorEmails: { reviewId: number };
};

export type ResultsStackParamList = {
  ResultsList: undefined;
  ResultDetail: { reviewId: number };
  ItemDetail: { reviewId: number; itemId: number; categoryName: string };
  VendorEmails: { reviewId: number };
};

export type ProfileStackParamList = {
  ProfileMain: undefined;
  ChangePassword: undefined;
};

export type AppTabParamList = {
  Home: undefined;
  Reviews: undefined;
  Results: undefined;
  Profile: undefined;
  Admin: undefined;
};

const Tab = createBottomTabNavigator<AppTabParamList>();
const ReviewStack = createStackNavigator<ReviewStackParamList>();
const ResultsStack = createStackNavigator<ResultsStackParamList>();
const ProfileStack = createStackNavigator<ProfileStackParamList>();

// Icon component that works on both web and mobile
export const TabIcon = ({ name, focused }: { name: string; focused: boolean }) => {
  if (Platform.OS === 'web') {
    const iconMap: { [key: string]: string } = {
      'home': focused ? '🏠' : '🏡',
      'home-outline': '🏡',
      'clipboard-text': focused ? '📋' : '📄',
      'clipboard-text-outline': '📄',
      'chart-line': focused ? '📊' : '📈',
      'account': focused ? '👤' : '👥',
      'account-outline': '👥',
      'cog': focused ? '⚙️' : '🔧',
    };
    return <Text style={{ fontSize: 20 }}>{iconMap[name] || '❓'}</Text>;
  }
  
  return (
    <MaterialCommunityIcons
      name={name}
      size={24}
      color={focused ? colors.primary : colors.gray500}
    />
  );
};

const ReviewNavigator: React.FC = () => {
  return (
    <ReviewStack.Navigator
      screenOptions={{
        headerStyle: {
          backgroundColor: Platform.OS === 'web' ? '#fff' : colors.primary,
          borderBottomWidth: Platform.OS === 'web' ? 1 : 0,
          borderBottomColor: Platform.OS === 'web' ? colors.gray200 : undefined,
        },
        headerTintColor: Platform.OS === 'web' ? colors.primary : '#fff',
        headerTitleStyle: {
          fontWeight: 'bold',
          color: Platform.OS === 'web' ? colors.text : '#fff',
        },
        headerBackImage: Platform.OS === 'web' ? () => (
          <Text style={{ fontSize: 24, color: colors.primary, marginLeft: 10 }}>←</Text>
        ) : undefined,
      }}
    >
      <ReviewStack.Screen
        name="ReviewList"
        component={ReviewListScreen}
        options={{ title: 'Reviews' }}
      />
      <ReviewStack.Screen
        name="ReviewDetail"
        component={ReviewDetailScreen}
        options={({ route }) => ({
          title: 'Review Details',
          headerRight: () => (
            <SearchTrigger 
              variant="header" 
              iconColor={Platform.OS === 'web' ? colors.primary : '#fff'} 
              reviewId={route.params.reviewId}
              navigationContext="Reviews"
              testID="review-header-search-button"
            />
          ),
        })}
      />
      <ReviewStack.Screen
        name="ItemDetail"
        component={ItemDetailScreen}
        options={({ route }) => ({
          title: route.params.categoryName,
          headerRight: () => (
            <SearchTrigger 
              variant="header" 
              iconColor={Platform.OS === 'web' ? colors.primary : '#fff'} 
              reviewId={route.params.reviewId}
              navigationContext="Reviews"
              testID="review-header-search-button"
            />
          ),
        })}
      />
      <ReviewStack.Screen
        name="VendorEmails"
        component={VendorEmailsScreen}
        options={{
          title: 'Vendor Emails',
          headerShown: true // Show the standard header for consistency
        }}
      />
    </ReviewStack.Navigator>
  );
};

const ResultsNavigator: React.FC = () => {
  return (
    <ResultsStack.Navigator
      screenOptions={{
        headerStyle: {
          backgroundColor: Platform.OS === 'web' ? '#fff' : colors.primary,
          borderBottomWidth: Platform.OS === 'web' ? 1 : 0,
          borderBottomColor: Platform.OS === 'web' ? colors.gray200 : undefined,
        },
        headerTintColor: Platform.OS === 'web' ? colors.primary : '#fff',
        headerTitleStyle: {
          fontWeight: 'bold',
          color: Platform.OS === 'web' ? colors.text : '#fff',
        },
        headerBackImage: Platform.OS === 'web' ? () => (
          <Text style={{ fontSize: 24, color: colors.primary, marginLeft: 10 }}>←</Text>
        ) : undefined,
      }}
    >
      <ResultsStack.Screen
        name="ResultsList"
        component={ResultsScreen}
        options={{ title: 'Review Results' }}
      />
      <ResultsStack.Screen
        name="ResultDetail"
        component={ResultDetailScreen}
        options={{ title: 'Review Summary' }}
      />
      <ResultsStack.Screen
        name="ItemDetail"
        component={ItemDetailScreen}
        options={({ route }) => ({
          title: route.params.categoryName,
          headerRight: () => (
            <SearchTrigger 
              variant="header" 
              iconColor={Platform.OS === 'web' ? colors.primary : '#fff'} 
              reviewId={route.params.reviewId}
              navigationContext="Results"
              testID="review-header-search-button"
            />
          ),
        })}
      />
      <ResultsStack.Screen
        name="VendorEmails"
        component={VendorEmailsScreen}
        options={{ title: 'Vendor Emails' }}
      />
    </ResultsStack.Navigator>
  );
};

const ProfileNavigator: React.FC = () => {
  return (
    <ProfileStack.Navigator
      screenOptions={{
        headerStyle: {
          backgroundColor: Platform.OS === 'web' ? '#fff' : colors.primary,
          borderBottomWidth: Platform.OS === 'web' ? 1 : 0,
          borderBottomColor: Platform.OS === 'web' ? colors.gray200 : undefined,
        },
        headerTintColor: Platform.OS === 'web' ? colors.primary : '#fff',
        headerTitleStyle: {
          fontWeight: 'bold',
          color: Platform.OS === 'web' ? colors.text : '#fff',
        },
        headerBackImage: Platform.OS === 'web' ? () => (
          <Text style={{ fontSize: 24, color: colors.primary, marginLeft: 10 }}>←</Text>
        ) : undefined,
      }}
    >
      <ProfileStack.Screen
        name="ProfileMain"
        component={ProfileScreen}
        options={{ title: 'Profile' }}
      />
      <ProfileStack.Screen
        name="ChangePassword"
        component={ChangePasswordScreen}
        options={{ title: 'Change Password' }}
      />
    </ProfileStack.Navigator>
  );
};

const AppNavigator: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const isAdmin = user?.role === 'admin';
  
  return (
    <Tab.Navigator
      screenOptions={({ route }) => ({
        tabBarIcon: ({ focused }) => {
          let iconName = 'home';

          if (route.name === 'Home') {
            iconName = focused ? 'home' : 'home-outline';
          } else if (route.name === 'Reviews') {
            iconName = focused ? 'clipboard-text' : 'clipboard-text-outline';
          } else if (route.name === 'Results') {
            iconName = focused ? 'chart-line' : 'chart-line';
          } else if (route.name === 'Profile') {
            iconName = focused ? 'account' : 'account-outline';
          } else if (route.name === 'Admin') {
            iconName = 'cog';
          }

          return <TabIcon name={iconName} focused={focused} />;
        },
        tabBarActiveTintColor: colors.primary,
        tabBarInactiveTintColor: colors.textSecondary,
        headerStyle: {
          backgroundColor: Platform.OS === 'web' ? '#fff' : colors.primary,
          borderBottomWidth: Platform.OS === 'web' ? 1 : 0,
          borderBottomColor: Platform.OS === 'web' ? colors.gray200 : undefined,
        },
        headerTintColor: Platform.OS === 'web' ? colors.primary : '#fff',
        headerTitleStyle: {
          fontWeight: 'bold',
          color: Platform.OS === 'web' ? colors.text : '#fff',
        },
      })}
    >
      <Tab.Screen
        name="Home"
        component={HomeScreen}
        options={{ tabBarTestID: 'tab-home' }}
      />
      <Tab.Screen
        name="Reviews"
        component={ReviewNavigator}
        options={{ headerShown: false, tabBarTestID: 'tab-reviews' }}
      />
      <Tab.Screen
        name="Results"
        component={ResultsNavigator}
        options={{ headerShown: false, tabBarTestID: 'tab-results' }}
      />
      <Tab.Screen
        name="Profile"
        component={ProfileNavigator}
        options={{ headerShown: false, tabBarTestID: 'tab-profile' }}
      />
      {isAdmin && (
        <Tab.Screen
          name="Admin"
          component={AdminScreen}
          options={{ tabBarTestID: 'tab-admin' }}
        />
      )}
    </Tab.Navigator>
  );
};

export default AppNavigator;
